'use client'

import { Facebook, Linkedin, Mail, Twitter, X, ZoomIn } from 'lucide-react'
import { useTranslations } from 'next-intl'
import NextImage from 'next/image'
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/utilities/ui'

const MIN_SCALE = 1
const MAX_SCALE = 5

/**
 * Flag "montado no cliente" sem setState-em-effect (regra react-hooks/set-state-in-effect):
 * `useSyncExternalStore` devolve `false` no SSR/hidratação (getServerSnapshot) e `true` no cliente,
 * mantendo o portal fora do HTML do servidor sem mismatch.
 */
const noopSubscribe = () => () => {}
const useMounted = () =>
  useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  )

type Props = {
  src: string
  alt: string
  width?: number
  height?: number
  imgClassName?: string
  priority?: boolean
  sizes?: string
}

/**
 * Renderiza a imagem com cursor de zoom e abre um overlay full-screen ao clicar,
 * com suporte a scroll-to-zoom, drag-to-pan, pinch-to-zoom e barra de partilha mobile.
 */
const DEFAULT_SIZES = '(max-width: 768px) 100vw, (max-width: 1280px) min(92vw, 1100px), 1200px'

export function MediaZoomWrapper({
  src,
  alt,
  width,
  height,
  imgClassName,
  priority,
  sizes,
}: Props) {
  const t = useTranslations('Media')
  const mounted = useMounted()
  const [open, setOpen] = useState(false)
  const [scale, setScale] = useState(1)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })

  const containerRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const lastMouse = useRef({ x: 0, y: 0 })
  const lastTouchDist = useRef<number | null>(null)
  // stable refs para os handlers nativos (evitam re-attach desnecessário)
  const scaleRef = useRef(scale)
  // Atualiza o ref num effect (não no render — regra react-hooks/refs).
  useEffect(() => {
    scaleRef.current = scale
  }, [scale])

  const close = useCallback(() => {
    setOpen(false)
    setScale(1)
    setTranslate({ x: 0, y: 0 })
  }, [])

  // Fechar com Escape
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, close])

  // Bloquear scroll do body quando o overlay está aberto
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  // ── Wheel e touch com { passive: false } para poder chamar preventDefault
  useEffect(() => {
    const el = containerRef.current
    if (!el || !open) return

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const delta = -e.deltaY * 0.002
      setScale((s) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s + delta)))
    }

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX
        const dy = e.touches[0].clientY - e.touches[1].clientY
        lastTouchDist.current = Math.hypot(dx, dy)
      } else if (e.touches.length === 1) {
        isDragging.current = true
        lastMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault()
      if (e.touches.length === 2 && lastTouchDist.current !== null) {
        const dx = e.touches[0].clientX - e.touches[1].clientX
        const dy = e.touches[0].clientY - e.touches[1].clientY
        const dist = Math.hypot(dx, dy)
        const delta = (dist - lastTouchDist.current) / 200
        setScale((s) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s + delta)))
        lastTouchDist.current = dist
      } else if (e.touches.length === 1 && isDragging.current) {
        const dx = e.touches[0].clientX - lastMouse.current.x
        const dy = e.touches[0].clientY - lastMouse.current.y
        lastMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
        setTranslate((t) => ({ x: t.x + dx / scaleRef.current, y: t.y + dy / scaleRef.current }))
      }
    }

    const onTouchEnd = () => {
      isDragging.current = false
      lastTouchDist.current = null
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    el.addEventListener('touchstart', onTouchStart, { passive: false })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd)

    return () => {
      el.removeEventListener('wheel', onWheel)
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
    }
  }, [open])

  // ── Mouse drag (sintético React — sem necessidade de passive)
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true
    lastMouse.current = { x: e.clientX, y: e.clientY }
  }, [])

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return
    const dx = e.clientX - lastMouse.current.x
    const dy = e.clientY - lastMouse.current.y
    lastMouse.current = { x: e.clientX, y: e.clientY }
    setTranslate((t) => ({ x: t.x + dx / scaleRef.current, y: t.y + dy / scaleRef.current }))
  }, [])

  const onMouseUp = useCallback(() => {
    isDragging.current = false
  }, [])

  // URLs de partilha (apenas client-side)
  const pageUrl = typeof window !== 'undefined' ? encodeURIComponent(window.location.href) : ''
  const encodedAlt = encodeURIComponent(alt)
  const hasSize = Boolean(width && height)
  const isSvgSrc = /\.svg(\?|#|$)/i.test(src)
  // Mídia R2 (URL absoluta) atrás da Cloudflare → 403 no otimizador do Next; servir direto da CDN.
  const skipOptimizer = isSvgSrc || /^https?:\/\//i.test(src)
  const resolvedSizes = sizes ?? DEFAULT_SIZES

  return (
    <>
      {/* ── Thumbnail clicável */}
      <div
        role="button"
        tabIndex={0}
        aria-label={t('zoomImage', { alt })}
        className="group relative max-w-full min-w-0 cursor-zoom-in rounded-(--post-thumb-radius,0px) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setOpen(true)
        }}
      >
        {hasSize ? (
          <NextImage
            alt={alt}
            className={cn('h-auto max-w-full rounded-(--post-thumb-radius,0px)', imgClassName)}
            height={height}
            loading={priority ? undefined : 'lazy'}
            priority={priority}
            quality={skipOptimizer ? undefined : 100}
            sizes={resolvedSizes}
            src={src}
            unoptimized={skipOptimizer}
            width={width}
          />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            alt={alt}
            className={cn('h-auto max-w-full rounded-(--post-thumb-radius,0px)', imgClassName)}
            decoding="async"
            loading={priority ? 'eager' : 'lazy'}
            src={src}
          />
        )}

        {/* Badge de zoom */}
        <span
          className="pointer-events-none absolute right-2 bottom-2 flex size-7 items-center justify-center rounded-full bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100"
          aria-hidden
        >
          <ZoomIn className="size-3.5" />
        </span>
      </div>

      {/* ── Overlay de zoom (portal) */}
      {mounted &&
        open &&
        createPortal(
          <div
            role="dialog"
            aria-modal
            aria-label={t('enlargedImage', { alt })}
            className="fixed inset-0 z-1001 overflow-y-auto bg-background/92 backdrop-blur-sm"
          >
            {/* Botão fechar */}
            <button
              type="button"
              aria-label={t('closeZoom')}
              onClick={close}
              className="fixed right-4 top-4 z-1002 flex size-10 items-center justify-center rounded-full bg-background/80 text-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:right-6 sm:top-6"
            >
              <X className="size-5" aria-hidden />
            </button>

            {/* Área da imagem — listeners nativos via ref */}
            <div
              ref={containerRef}
              className="mx-auto flex max-w-[1010px] select-none items-center justify-center overflow-hidden touch-none px-0.5 sm:px-0"
              style={{ height: 'calc(100% - 96px)' }}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                alt={alt}
                src={src}
                draggable={false}
                className="max-w-full rounded-(--post-thumb-radius,0px) border border-border"
                style={{
                  cursor: scale > 1 ? 'grab' : 'zoom-in',
                  transformOrigin: '0 0',
                  transform: `scale3d(${scale},${scale},1) translate3d(${translate.x}px,${translate.y}px,0)`,
                }}
              />
            </div>

            {/* Barra de partilha mobile */}
            <div className="fixed inset-x-0 bottom-0 flex h-24 items-center justify-center rounded-t-[10px] border-t border-border bg-card px-6 sm:hidden">
              <div className="flex flex-row gap-2">
                {(
                  [
                    {
                      href: `https://linkedin.com/sharing/share-offsite?url=${pageUrl}`,
                      label: t('shareLinkedIn'),
                      icon: <Linkedin className="size-4" aria-hidden />,
                    },
                    {
                      href: `https://twitter.com/intent/tweet?url=${pageUrl}&text=${encodedAlt}`,
                      label: t('shareX'),
                      icon: <Twitter className="size-4" aria-hidden />,
                    },
                    {
                      href: `https://facebook.com/sharer/sharer.php?u=${pageUrl}`,
                      label: t('shareFacebook'),
                      icon: <Facebook className="size-4" aria-hidden />,
                    },
                    {
                      href: `mailto:?body=${encodedAlt}%0D%0A${pageUrl}`,
                      label: t('shareEmail'),
                      icon: <Mail className="size-4" aria-hidden />,
                    },
                  ] as const
                ).map(({ href, label, icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    aria-label={label}
                    className="flex size-10 items-center justify-center rounded-md bg-muted text-foreground transition-colors hover:bg-muted/70"
                  >
                    {icon}
                  </a>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
